import React, { useState } from "react";
import { toast } from "sonner";
import { Broom } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { fmtDate, gbp } from "./bookingFormat";

const Item = ({ checked, onToggle, testId, title, sub }) => (
  <label className="flex items-start gap-2 py-1.5 text-sm cursor-pointer">
    <input type="checkbox" checked={checked} onChange={onToggle} data-testid={testId} className="mt-1 accent-[#8C6643]" />
    <span><span className="text-ink">{title}</span><span className="block text-xs text-ink-muted">{sub}</span></span>
  </label>
);

const Group = ({ title, items, picked, toggle, render, prefix }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.16em] text-ink-muted mb-1">{title} ({items.length})</div>
    {items.length === 0 && <p className="text-sm text-ink-muted/70">None found</p>}
    {items.map((x) => <Item key={x.id} checked={picked.has(x.id)} onToggle={() => toggle(x.id)} testId={`${prefix}-${x.id}`} {...render(x)} />)}
  </div>
);

export const TestDataCleanup = ({ onDone }) => {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const open = async () => {
    try {
      const { data: d } = await api.get("/admin/test-data");
      setData(d);
      setPicked(new Set([...d.enquiries, ...d.bookings].map((x) => x.id)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const purge = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/test-data/purge", {
        enquiry_ids: data.enquiries.filter((e) => picked.has(e.id)).map((e) => e.id),
        booking_ids: data.bookings.filter((b) => picked.has(b.id)).map((b) => b.id),
      });
      toast.success(`Deleted ${r.enquiries_deleted} test messages and ${r.bookings_deleted} test bookings.`);
      setData(null);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={open} data-testid="test-data-cleanup-btn" className="btn-secondary inline-flex items-center gap-1.5"><Broom size={16} /> Clean up test data</button>
      <Dialog open={!!data} onOpenChange={(o) => !o && setData(null)}>
        <DialogContent data-testid="test-data-dialog" className="bg-cream-50 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Remove test data</DialogTitle>
            <DialogDescription>Items with test-looking names or emails (e.g. @example.com, &quot;test&quot;). Untick anything that&apos;s real. Deleting can&apos;t be undone.</DialogDescription>
          </DialogHeader>
          {data && (
            <div className="space-y-5">
              <Group title="Contact messages" items={data.enquiries} picked={picked} toggle={toggle} prefix="test-enquiry"
                render={(e) => ({ title: `${e.name} · ${e.email}`, sub: `${fmtDate(e.created_at)} · ${e.summary}` })} />
              <Group title="Bookings" items={data.bookings} picked={picked} toggle={toggle} prefix="test-booking"
                render={(b) => ({ title: `${b.patient_name || "No name"} · ${b.patient_email}`, sub: `${b.booking_ref} · ${b.state} · ${b.amount ? gbp(b.amount) : "-"} · ${fmtDate(b.start_time)}` })} />
            </div>
          )}
          <DialogFooter>
            <button type="button" onClick={() => setData(null)} data-testid="test-data-close-btn" className="btn-secondary">Close</button>
            <button type="button" onClick={purge} disabled={busy || picked.size === 0} data-testid="test-data-purge-btn" className="btn-primary disabled:opacity-50">
              {busy ? "Deleting…" : `Delete ${picked.size} selected`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
